/**
 * DD0 UGC Videos — web components
 *
 * <dd0-ugc-video data-mode="facade|autoplay">
 *   Wraps a <video> + poster + play button. Lazy-loads <source data-src>
 *   on click (facade) or on IntersectionObserver enter (autoplay muted).
 *   Pauses on exit. Cleans observer on disconnect.
 *
 * <dd0-ugc-carousel>
 *   Native scroll-snap viewport + prev/next arrows. ~25 lines, no slider lib.
 */

(() => {
class DD0UGCVideo extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.video = this.querySelector('video');
    this.poster = this.querySelector('.dd0-ugc-video__poster');
    this.playBtn = this.querySelector('.dd0-ugc-video__play');
    this.soundBtn = this.querySelector('.dd0-ugc-video__sound');
    this.mode = this.dataset.mode || 'facade';
    this.nativeControls = this.dataset.nativeControls === 'true';
    this.loaded = !!this.video && !this.video.querySelector('source[data-src]') &&
      !!(this.video.getAttribute('src') || this.video.querySelector('source[src]'));

    if (this.playBtn) {
      this.playBtn.addEventListener('click', this.onPlayClick.bind(this), { signal });
    }
    if (this.soundBtn) {
      this.soundBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.onSoundClick();
      }, { signal });
    }

    if (!this.nativeControls) {
      this.addEventListener('click', (e) => {
        // Skip if click landed on an interactive child (play btn, sound btn, link, dot)
        if (e.target.closest('button, a, [data-dot-index]')) return;
        this.onVideoTap();
      }, { signal });
    }

    if (this.mode === 'autoplay') {
      this.io = new IntersectionObserver(this.onIntersect.bind(this), { threshold: 0.5 });
      this.io.observe(this);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.io?.disconnect();
    this.video?.pause();
    this.classList.remove('is-playing');
  }

  onPlayClick() {
    this.activate({ autoplay: false, unmute: true });
  }

  onVideoTap() {
    if (!this.video) return;
    if (!this.loaded) {
      // First tap loads sources and plays with sound (same as play button)
      this.activate({ autoplay: false, unmute: true });
      return;
    }
    if (this.video.paused) {
      this.video.play().catch(() => {});
      this.classList.add('is-playing');
    } else {
      this.video.pause();
      this.classList.remove('is-playing');
    }
  }

  onSoundClick() {
    if (!this.video) return;
    this.video.muted = !this.video.muted;
    this.updateSoundIcon();
  }

  onIntersect(entries) {
    if (!this.video || !this.isConnected) return;
    const visible = entries[0].isIntersecting;
    const carousel = this.closest('dd0-ugc-carousel');
    if (visible && carousel?.manualVideo && carousel.manualVideo !== this) return;
    if (visible) {
      if (carousel?.manualVideo === this) return;
      this.activate({ autoplay: true, unmute: false });
    } else if (this.loaded) {
      this.video.pause();
    }
  }

  activate({ autoplay, unmute }) {
    if (!this.video) return;
    if (!this.loaded) {
      this.querySelectorAll('source[data-src]').forEach((s) => {
        s.src = s.dataset.src;
        s.removeAttribute('data-src');
      });
      this.video.load();
      this.loaded = true;
    }
    if (unmute) {
      this.video.muted = false;
      if (this.nativeControls) this.video.controls = true;
    } else if (autoplay) {
      this.video.muted = true;
    }
    if (autoplay && this.dataset.infiniteIndex !== undefined) {
      const carousel = this.closest('dd0-ugc-carousel');
      const sync = () => {
        const equivalent = Array.from(carousel.querySelectorAll('dd0-ugc-video')).find((card) =>
          card !== this && card.dataset.infiniteIndex === this.dataset.infiniteIndex &&
          card.video?.readyState > 0 && !card.video.paused);
        if (equivalent && this.video.readyState > 0) this.video.currentTime = equivalent.video.currentTime;
      };
      if (this.video.readyState > 0) sync();
      else this.video.addEventListener('loadedmetadata', sync, { once: true, signal: this.controller.signal });
    }
    this.video.play().catch(() => {});
    this.classList.add('is-playing');
    this.updateSoundIcon();
  }

  updateSoundIcon() {
    if (!this.soundBtn || !this.video) return;
    this.soundBtn.dataset.muted = this.video.muted ? 'true' : 'false';
  }
}

class DD0UGCCarousel extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.viewport = this.querySelector('.dd0-ugc-videos__viewport');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.dots = Array.from(this.querySelectorAll('[data-dot-index]'));
    if (!this.viewport) return;

    this.prevBtn?.addEventListener('click', () => this.scrollByCard(-1), { signal });
    this.nextBtn?.addEventListener('click', () => this.scrollByCard(1), { signal });
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.scrollToIndex(parseInt(dot.dataset.dotIndex, 10)), { signal });
    });
    this.scrollRaf = null;
    this.viewport.addEventListener('scroll', () => {
      if (this.motionEnabled) return;
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        this.onScroll();
      });
    }, { passive: true, signal });

    this.bindDrag();
    this.bindWheel();
    if (this.dataset.infiniteScroll === 'true') this.bindInfiniteScroll();

    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = null;
      this.onScroll();
    });
    this.onWindowResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.measureInfiniteScroll?.();
        this.onScroll();
      }, 150);
    };
    window.addEventListener('resize', this.onWindowResize, { passive: true, signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.viewport?.classList.remove('is-dragging');
    if (this.onWindowResize) window.removeEventListener('resize', this.onWindowResize);
    clearTimeout(this.resizeTimer);
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
    this.scrollRaf = null;
    cancelAnimationFrame(this.motionRaf);
    this.motionRaf = null;
    this.motionObserver?.disconnect();
    this.motionResizeObserver?.disconnect();
    this.querySelectorAll('[data-infinite-copy]').forEach((card) => card.remove());
    this.originalCards?.forEach((card) => card.removeAttribute('data-infinite-index'));
    this.classList.remove('customer-videos--loop-ready');
    clearTimeout(this.motionResumeTimer);
    this.manualVideo = null;
  }

  bindInfiniteScroll() {
    this.originalCards = Array.from(this.viewport.children);
    if (this.originalCards.length < 2) return;
    this.motionSpeed = Math.min(100, Math.max(5, Number(this.dataset.scrollSpeed) || 24));
    this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
    this.motionPaused = false;
    this.motionHover = false;
    this.motionInteracting = false;
    this.motionPointerActive = false;
    this.motionVisible = false;
    this.motionEnabled = false;
    this.motionRaf = null;
    this.motionLastTime = null;
    this.motionTemplates = this.originalCards.map((card, index) => {
      card.dataset.infiniteIndex = String(index);
      const copy = card.cloneNode(true);
      copy.classList.remove('is-playing');
      for (const node of [copy, ...copy.querySelectorAll('*')]) {
        node.removeAttribute('id');
        for (const attribute of Array.from(node.attributes)) {
          if (attribute.name.startsWith('data-shopify')) node.removeAttribute(attribute.name);
        }
      }
      for (const source of copy.querySelectorAll('source[src]')) {
        source.dataset.src = source.getAttribute('src');
        source.removeAttribute('src');
      }
      // Repetitions keep mouse/touch actions, but only originals enter the Tab order.
      copy.setAttribute('aria-hidden', 'true');
      for (const node of copy.querySelectorAll('button, a, video, [tabindex]')) node.tabIndex = -1;
      return copy;
    });
    const { signal } = this.controller;
    const stopForInteraction = (event) => {
      this.motionInteracting = true;
      if (event.type === 'pointerdown') this.motionPointerActive = true;
      const card = event.target.closest('dd0-ugc-video');
      if (card?.video && ['click', 'play', 'volumechange'].includes(event.type)) {
        this.manualVideo = card;
        for (const other of this.viewport.querySelectorAll('dd0-ugc-video')) {
          if (other !== card) other.video?.pause();
        }
      }
      clearTimeout(this.motionResumeTimer);
      this.motionResumeTimer = setTimeout(() => {
        this.motionInteracting = false;
        this.updateMotion();
      }, 1200);
      this.updateMotion();
    };
    for (const type of ['pointerdown', 'click', 'wheel', 'focusin']) {
      this.viewport.addEventListener(type, stopForInteraction, { capture: true, passive: true, signal });
    }
    for (const type of ['pointerup', 'pointercancel']) {
      window.addEventListener(type, () => {
        this.motionPointerActive = false;
        this.updateMotion();
      }, { signal });
    }
    this.viewport.addEventListener('focusout', () => {
      queueMicrotask(() => {
        if (this.isConnected) this.updateMotion();
      });
    }, { signal });
    this.viewport.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      this.motionHover = true;
      this.updateMotion();
    }, { signal });
    this.viewport.addEventListener('pointerleave', () => {
      this.motionHover = false;
      this.updateMotion();
    }, { signal });
    this.viewport.addEventListener('play', (event) => {
      if (!event.target.muted) stopForInteraction(event);
    }, { capture: true, signal });
    this.viewport.addEventListener('volumechange', (event) => {
      if (!event.target.muted) stopForInteraction(event);
    }, { capture: true, signal });
    for (const type of ['pause', 'ended']) {
      this.viewport.addEventListener(type, (event) => {
        if (event.target !== this.manualVideo?.video) return;
        this.manualVideo = null;
        this.updateMotion();
      }, { capture: true, signal });
    }
    this.addEventListener('shopify:block:select', (event) => {
      const card = event.target.closest('dd0-ugc-video');
      if (!card || !this.originalCards.includes(card)) return;
      this.motionPaused = true;
      this.updateMotion();
      this.viewport.scrollLeft += card.getBoundingClientRect().left - this.viewport.getBoundingClientRect().left;
    }, { signal });
    this.addEventListener('shopify:block:deselect', () => {
      this.motionPaused = false;
      this.updateMotion();
    }, { signal });
    document.addEventListener('visibilitychange', () => this.updateMotion(), { signal });
    this.motionQuery.addEventListener('change', () => {
      this.measureInfiniteScroll();
      this.updateMotion();
    }, { signal });
    this.motionObserver = new IntersectionObserver(([entry]) => {
      this.motionVisible = entry.isIntersecting;
      this.updateMotion();
    });
    this.motionObserver.observe(this);
    this.motionResizeObserver = new ResizeObserver(() => this.measureInfiniteScroll());
    this.motionResizeObserver.observe(this.viewport);
    this.measureInfiniteScroll();
  }

  measureInfiniteScroll() {
    if (!this.originalCards || this.originalCards.length < 2) return;
    const [first, second] = this.originalCards;
    const step = second.getBoundingClientRect().left - first.getBoundingClientRect().left;
    if (step <= 0 || !this.viewport.clientWidth) return;
    this.motionCycle = step * this.originalCards.length;
    if (!this.motionQuery.matches) {
      const needed = Math.ceil(this.viewport.clientWidth / step) + 1;
      let count = this.viewport.querySelectorAll('[data-infinite-copy]').length;
      while (count < needed) {
        const index = count % this.originalCards.length;
        const copy = this.motionTemplates[index].cloneNode(true);
        copy.dataset.infiniteCopy = String(index);
        this.viewport.append(copy);
        count++;
      }
      this.classList.add('customer-videos--loop-ready');
      this.motionEnabled = true;
    }
    this.motionPosition = this.viewport.scrollLeft;
    this.updateMotion();
  }

  updateMotion() {
    if (!this.motionQuery) return;
    if (this.manualVideo?.video?.paused || this.manualVideo?.video?.ended) this.manualVideo = null;
    const manualPlaying = this.manualVideo?.video && !this.manualVideo.video.paused;
    const blocked = !this.motionEnabled || this.motionQuery.matches || this.motionPaused ||
      this.motionHover || this.motionInteracting || this.motionPointerActive || manualPlaying ||
      this.viewport.contains(document.activeElement) || !this.motionVisible || document.hidden;
    if (blocked) {
      cancelAnimationFrame(this.motionRaf);
      this.motionRaf = null;
      this.motionLastTime = null;
      return;
    }
    if (this.motionRaf !== null) return;
    this.motionPosition = this.viewport.scrollLeft;
    this.motionRaf = requestAnimationFrame((time) => this.advanceMotion(time));
  }

  advanceMotion(time) {
    this.motionRaf = null;
    if (this.motionLastTime !== null) {
      this.motionPosition += Math.min(time - this.motionLastTime, 100) * this.motionSpeed / 1000;
      if (this.motionPosition >= this.motionCycle) {
        // Match equivalent visible videos before resetting the native scroll offset.
        for (const copy of this.viewport.querySelectorAll('[data-infinite-copy]')) {
          const source = copy.video;
          const original = this.originalCards[Number(copy.dataset.infiniteCopy)];
          if (source && !source.paused && source.readyState > 0 && original.video?.readyState > 0) {
            original.video.currentTime = source.currentTime;
          }
        }
        this.motionPosition %= this.motionCycle;
      }
      this.viewport.scrollLeft = this.motionPosition;
    }
    this.motionLastTime = time;
    this.motionRaf = requestAnimationFrame((nextTime) => this.advanceMotion(nextTime));
  }

  bindDrag() {
    // Deferred pointer capture pattern: don't capture the pointer until movement
    // crosses the drag threshold. A pure click (no movement) flows normally to
    // children — preserving native click-to-pause on the <video>.
    const v = this.viewport;
    const THRESHOLD = 6;
    let armed = false;
    let dragging = false;
    let startX = 0;
    let startScroll = 0;
    let moved = 0;
    let pointerId = null;

    const onDown = (e) => {
      if (e.target.closest('button, a, [data-dot-index]')) return;
      if (e.button !== undefined && e.button !== 0) return;
      armed = true;
      dragging = false;
      moved = 0;
      startX = e.clientX;
      startScroll = v.scrollLeft;
      pointerId = e.pointerId;
    };

    const onMove = (e) => {
      if (!armed) return;
      const dx = e.clientX - startX;
      moved = Math.abs(dx);
      if (!dragging && moved > THRESHOLD) {
        dragging = true;
        v.classList.add('is-dragging');
        try { v.setPointerCapture?.(pointerId); } catch (_) {}
      }
      if (dragging) {
        v.scrollLeft = startScroll - dx;
        if (e.cancelable) e.preventDefault();
      }
    };

    const onUp = () => {
      if (!armed) return;
      armed = false;
      if (dragging) {
        dragging = false;
        v.classList.remove('is-dragging');
        const blockNextClick = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
        v.addEventListener('click', blockNextClick, { capture: true, once: true, signal: this.controller.signal });
      }
    };

    const { signal } = this.controller;
    v.addEventListener('pointerdown', onDown, { signal });
    v.addEventListener('pointermove', onMove, { signal });
    v.addEventListener('pointerup', onUp, { signal });
    v.addEventListener('pointercancel', onUp, { signal });
  }

  bindWheel() {
    const v = this.viewport;
    v.addEventListener('wheel', (e) => {
      // Only redirect when carousel can scroll horizontally AND the gesture is primarily vertical wheel
      const canScrollX = v.scrollWidth > v.clientWidth + 1;
      if (!canScrollX) return;
      // Trackpad horizontal gestures already produce deltaX — let them through natively
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      if (e.deltaY === 0) return;
      e.preventDefault();
      v.scrollBy({ left: e.deltaY, behavior: 'auto' });
    }, { passive: false, signal: this.controller.signal });
  }

  cardStep() {
    const card = this.viewport.querySelector('.dd0-ugc-video');
    if (!card) return 0;
    const gap = parseFloat(getComputedStyle(this.viewport).columnGap || getComputedStyle(this.viewport).gap || 0) || 0;
    return card.offsetWidth + gap;
  }

  scrollByCard(dir) {
    const step = this.cardStep();
    if (!step) return;
    this.viewport.scrollBy({ left: dir * step, behavior: 'smooth' });
  }

  scrollToIndex(idx) {
    const card = this.viewport.querySelectorAll('.dd0-ugc-video')[idx];
    if (!card) return;
    const pad = parseFloat(getComputedStyle(this.viewport).paddingInlineStart || 0) || 0;
    this.viewport.scrollTo({ left: card.offsetLeft - pad, behavior: 'smooth' });
  }

  onScroll() {
    this.updateArrows();
    this.updateDots();
  }

  updateArrows() {
    if (!this.prevBtn || !this.nextBtn) return;
    const v = this.viewport;
    const atStart = v.scrollLeft <= 4;
    const atEnd = v.scrollLeft >= v.scrollWidth - v.clientWidth - 4;
    this.prevBtn.toggleAttribute('disabled', atStart);
    this.nextBtn.toggleAttribute('disabled', atEnd);
  }

  updateDots() {
    if (!this.dots.length) return;
    const step = this.cardStep();
    if (!step) return;
    const active = Math.round(this.viewport.scrollLeft / step);
    this.dots.forEach((dot, i) => {
      const isActive = i === active;
      dot.classList.toggle('is-active', isActive);
      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('dd0-ugc-video')) {
  customElements.define('dd0-ugc-video', DD0UGCVideo);
}
if (!customElements.get('dd0-ugc-carousel')) {
  customElements.define('dd0-ugc-carousel', DD0UGCCarousel);
}
})();
